import type { QuestionnaireTurnCommandKind } from "./chat-turn";
import type { TextMaterialFamily } from "./asset-families";
import type { StudyEvidenceContract, StudyGoals } from "./index";
import type { StudyObjectiveChange } from "./revision.generated";

/**
 * A question the builder asked and is waiting on (plan 09 S4.1, L20–L22).
 *
 * One typed record in the chat transcript, append-only, with at most one
 * `open`. Only a producer's typed clarify outcome opens one, never a message
 * kind: an opening-chain step, a pre-Build gate, PLAN (which also asks about
 * the user's material), a revision or restructure `clarify`, or an
 * objective-panel restructure that asks. The next user turn closes it (`answered`, or
 * `superseded` by a command that does not answer it); three UI intercepts
 * close it by id in the commit they already make.
 *
 * An open record blocks Review (readiness `clarification_open`) and Launch
 * (409 `clarification_open`, the readiness code). Nest owns the transcript; Python
 * never reads it, and receives only the typed `answering` block.
 */
export const QUESTIONNAIRE_CLARIFICATION_ORIGINS = [
  "chain",
  "gate",
  "build",
  "revision",
  "restructure",
  "objective_panel",
] as const;

export type QuestionnaireClarificationOrigin =
  (typeof QUESTIONNAIRE_CLARIFICATION_ORIGINS)[number];

/**
 * What the question asks the user for: the material of a family the user gives
 * as words (claims, messages, pains, concepts), so a typed reply, links
 * included, is that material. It carries nothing of the answer and selects no
 * stage: PLAN reads the whole conversation on the turn it is answered, and a
 * message that is not an answer reaches the same producer as with no record
 * open (extractor fold, NA-18).
 */
export interface QuestionnaireMaterialWantedPending {
  kind: "material_wanted";
  assetType: TextMaterialFamily;
}

/** The objective the panel set, while its restructure waits on an answer. */
export interface QuestionnaireSetObjectivePending {
  kind: "set_objective";
  studyGoals: StudyGoals;
  studyEvidence?: StudyEvidenceContract;
  /** The edit's change, as its turn's enqueue recorded it; the rerun reads it. */
  change: StudyObjectiveChange;
}

export type QuestionnaireClarificationPending =
  | QuestionnaireMaterialWantedPending
  | QuestionnaireSetObjectivePending;

export interface QuestionnaireClarificationClosedBy {
  /** The turn that closed it; null for a UI intercept. */
  turnId: string | null;
  /** The user message that closed it; null for a UI intercept. */
  messageId: string | null;
  /** The option chosen on the card, when one was. */
  optionId?: string;
}

export interface QuestionnaireClarification {
  /** Minted by the writer. */
  id: string;
  origin: QuestionnaireClarificationOrigin;
  /** The chain-step or gate code; null for a model's question. */
  code: string | null;
  /** The assistant message that asks: its text and options live there. */
  messageId: string | null;
  /** The user message it asks about; null for chain, gate and panel records. */
  requestMessageId: string | null;
  pending: QuestionnaireClarificationPending | null;
  status: "open" | "answered" | "superseded";
  /** The draft revision the question was asked against. */
  askedAtRevision: number;
  closedBy?: QuestionnaireClarificationClosedBy;
}

/**
 * What a turn answers, as PLAN, `/revise` and `/re-plan` receive it: the
 * question's own text and the request it asked about. The turn's message is
 * the answer (`latestUserMessage`).
 */
export interface QuestionnaireAnswering {
  question: string;
  originalRequest: string | null;
}

interface WithClarifications {
  clarifications?: Array<QuestionnaireClarification>;
}

/** The one open record, or null. */
export function openClarificationOf(
  transcript: WithClarifications | null | undefined,
): QuestionnaireClarification | null {
  return (
    transcript?.clarifications?.find((record) => record.status === "open") ??
    null
  );
}

/** Append `record` as the open one; a record still open is superseded. */
export function openClarification<T extends WithClarifications>(
  transcript: T,
  record: QuestionnaireClarification,
): T {
  const records = (transcript.clarifications ?? []).map((existing) =>
    existing.status === "open"
      ? {
          ...existing,
          status: "superseded" as const,
          closedBy: { turnId: null, messageId: null },
        }
      : existing,
  );
  return { ...transcript, clarifications: [...records, record] };
}

/** Close the record with `id`; any other record is left as it is. */
export function closeClarification<T extends WithClarifications>(
  transcript: T,
  id: string,
  closedBy: QuestionnaireClarificationClosedBy,
  status: "answered" | "superseded",
): T {
  return {
    ...transcript,
    clarifications: (transcript.clarifications ?? []).map((record) =>
      record.id === id && record.status === "open"
        ? { ...record, status, closedBy }
        : record,
    ),
  };
}

/**
 * Whether a turn answers the open record or supersedes it (plan S4.1, S4.2):
 * a message answers it, a collect receipt answers a collect card, and any
 * other command supersedes it. Nest closes the record by this rule when the
 * turn commits; the builder shows the answer of a turn still in flight by the
 * same rule.
 */
export function turnAnswersClarification(
  commandKind: QuestionnaireTurnCommandKind | null | undefined,
  card: { collect?: unknown } | null | undefined,
): boolean {
  return (
    !commandKind ||
    (commandKind === "collect_receipt" && Boolean(card?.collect))
  );
}

/**
 * Whether a collect receipt fits the card it answers (F19): a receipt that
 * names nothing and collects nothing passes an attach card, or an optional
 * one, without a file. A card that collects the study's material (ads,
 * pages) has nothing to receive without one, so such a receipt there is
 * refused before it is queued. A receipt that collects (`collect_assets`) is
 * named by the server once its operation is applied.
 */
export function collectReceiptFitsCard(
  receipt: {
    assetKeys: ReadonlyArray<string>;
    assetOperations?: ReadonlyArray<{ op: string }>;
  },
  card: { collect?: { attachTo?: unknown; optional?: unknown } | null } | null | undefined,
): boolean {
  return (
    receipt.assetKeys.length > 0 ||
    Boolean(receipt.assetOperations?.some((operation) => operation.op === "collect_assets")) ||
    Array.isArray(card?.collect?.attachTo) ||
    card?.collect?.optional === true
  );
}
